import { useItemStore } from "@/itemStore";
import type { Item } from "@/itemStore";
import { processSettings } from "@/utils";

const baseUrl = import.meta.env.DEV ? "https://localhost/wp-json/jcore/v1" : "/wp-json/jcore/v1";

const init = {
  headers: {
    "Content-Type": "application/json",
  },
};

type SearchResponse = {
  total: number;
  items: Array<Item>;
};

export function searchPosts() {
  const itemStore = useItemStore();

  const urlParams = new URLSearchParams();
  console.debug(urlParams);

  urlParams.append("lang", itemStore.lang);
  urlParams.append("search", itemStore.search);
  urlParams.append("sort", itemStore.settings.sortBy);
  urlParams.append("order", itemStore.settings.asc ? "asc" : "desc");
  urlParams.append("page", itemStore.page.toString());

  for (const taxonomy of Object.keys(itemStore.filter)) {
    urlParams.append(taxonomy, itemStore.filter[taxonomy].join(","));
  }

  return fetch(`${baseUrl}/${itemStore.type}/search?${urlParams}`, init)
    .then((response) => response.json())
    .then((data) => {
      // Loop all response items and add them to store.
      (data as SearchResponse).items.forEach((item) => {
        itemStore.addItem(item);
      });
      // Update total only if it's above 0, or page is 1.
      if (data.total > 0 || data.page < 2) {
        itemStore.total = data.total;
      }
      if (data.total === 0) {
        // Don't do any more automatic page fetches for this query.
        itemStore.page = 0;
      }
    });
}

export function fetchMonth() {
  const itemStore = useItemStore();

  const urlParams = new URLSearchParams();
  urlParams.append("lang", itemStore.lang);

  return fetch(`${baseUrl}/${itemStore.type}/months?${urlParams}`, init)
    .then((response) => response.json())
    .then((data) => {
      itemStore.months = data;
    });
}

export function fetchSettings(): Promise<void> {
  const itemStore = useItemStore();

  const urlParams = new URLSearchParams();
  urlParams.append("lang", itemStore.lang);

  return fetch(`${baseUrl}/${itemStore.type}/settings?${urlParams}`, init)
    .then((response) => response.json())
    .then((data) => {
      for (const key of Object.keys(data)) {
        const value = data[key];
        switch (key) {
          case "show_search":
            itemStore.settings.showSearch = value;
            break;
          case "show_sort":
            itemStore.settings.showSort = value;
            break;
          case "show_preview":
            itemStore.settings.showPreview = value;
            break;
          case "sort_by":
            itemStore.settings.sortBy = value;
            break;
          case "fields":
            itemStore.settings.showFields = value.split(",") ?? [];
            break;
          case "radio":
            itemStore.settings.filterRadio = value;
            break;
          case "masonry":
            itemStore.settings.masonry = value;
            break;
          case "columns":
            itemStore.settings.columns = Number(value);
            break;
          case "read_more":
            itemStore.settings.readMore = value;
            break;
          case "per_page":
            itemStore.settings.perPage = Number(value);
            itemStore.show = Number(value);
            break;
          case "has_link":
            itemStore.settings.hasLink = value;
            break;
          case "full_link":
            itemStore.settings.fullLink = value;
            break;
          case "month":
            itemStore.settings.showMonth = value;
            break;
          case "terms":
            for (const taxonomy of Object.keys(value)) {
              const terms = value[taxonomy];
              itemStore.addTerms(terms, taxonomy);
            }
            break;
          default:
            console.debug(key, value);
        }
      }
      // Set initial value.
      itemStore.settings.asc = itemStore.settings.sortBy !== "date";

      processSettings(itemStore.settings);
    });
}
